import { useRouter } from 'next/router';
import ErrorPage from 'next/error';
import { useState, useRef, useEffect } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@helpers/async';
import { initializeApollo, addApolloState } from '../../apollo-client';
import { Box, Heading, Text, Link, Grid, GridItem } from '@terminal/design-system';
import NextNprogress from 'nextjs-progressbar';
import { NextSeo } from 'next-seo';

import SidebarLayout from '@components/resources/SidebarLayout';
import Card from '@components/resources/Card/Card';
import UpcomingWebinar from '@components/resources/Webinar/Upcoming';
import CATEGORIES from '@components/resources/Categories';
import sharecardImage from '@public/images/resources/sharecard.jpg';
import {
  primaryCategory,
  filterPostsByCategory,
  filterSinglePostByCategory,
} from '@helpers/categoryFilters';
import { addParamsToRelatedPost } from '@helpers/addParamsToRelatedPost';
import infiniteScroll from '@components/resources/infiniteScroll';
import sitePath from '@helpers/sitePath';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import metaRobots, { determineMetaRobots } from '@helpers/metaRobots';

// IMPORTANT: Keep the `first: 1000` or Wordpress will limit to 10 results
const GET_POSTS = gql`
  query Resources {
    posts(first: 1000, where: { categoryName: "Demand" }) {
      nodes {
        id
        date
        slug
        title
        categories {
          edges {
            isPrimary
            node {
              name
            }
          }
        }
        featuredImage {
          node {
            altText
            sourceUrl
          }
        }
      }
    }
    guides(first: 1000, where: { categoryName: "Demand" }) {
      nodes {
        id
        date
        slug
        title
        categories {
          edges {
            isPrimary
            node {
              name
              count
            }
          }
        }
        featuredImage {
          node {
            altText
            sourceUrl
          }
        }
        guidePostType {
          guideType
          externalLink {
            url
          }
        }
      }
    }
    webinars(first: 1000, where: { categoryName: "Demand" }) {
      nodes {
        id
        date
        slug
        title
        categories {
          edges {
            isPrimary
            node {
              name
            }
          }
        }
        featuredImage {
          node {
            altText
            sourceUrl
          }
        }
        webinarsACF {
          date
          time
          text
          speakers {
            name
            jobTitle
            image {
              sourceUrl
            }
          }
        }
      }
    }
    videos(first: 1000, where: { categoryName: "Demand" }) {
      nodes {
        id
        date
        slug
        title
        categories {
          edges {
            isPrimary
            node {
              name
              count
            }
          }
        }
        featuredImage {
          node {
            altText
            sourceUrl
          }
        }
      }
    }
    unlocks(first: 1000) {
      nodes {
        id
        date
        slug
        title
        categories {
          edges {
            isPrimary
            node {
              name
            }
          }
        }
        featuredImage {
          node {
            altText
            sourceUrl
          }
        }
      }
    }
    learnArticles(first: 1000) {
      nodes {
        id
        date
        slug
        title
        featuredImage {
          node {
            altText
            sourceUrl
          }
        }
        categories {
          edges {
            isPrimary
            node {
              name
            }
          }
        }
      }
    }
  }
`;

export async function getServerSideProps({ locale }) {
  const apolloClient = initializeApollo();

  await apolloClient.query({
    query: GET_POSTS,
  });

  return addApolloState(apolloClient, {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  });
}

export default function Resources() {
  const router = useRouter();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  let renderPosts = [];

  // infinite scroll
  const [itemsToShow, setItemsToShow] = useState(3);

  const itemsEnd = useRef(null);

  useEffect(() => {
    infiniteScroll(renderPosts, itemsEnd, itemsToShow, setItemsToShow);
  }, [renderPosts, itemsToShow]);

  // Client side Apollo
  const { loading, error, data } = useQuery(GET_POSTS);

  // CSR loading
  if (loading)
    return (
      <NextNprogress
        color="#2D445D"
        startPosition={0.3}
        stopDelayMs={200}
        height={3}
        showOnShallow={true}
      />
    );
  // Apollo error
  if (error) return <ErrorPage statusCode="500" />;

  // get all post types
  const allResources = [].concat(
    data?.posts.nodes,
    data?.guides.nodes,
    data?.webinars.nodes,
    data?.videos.nodes,
    data?.unlocks.nodes,
    data?.learnArticles.nodes,
  );

  // sort by date
  const allSortedResources = allResources.sort((a, b) => (a.date < b.date ? 1 : -1));

  // add path for post type
  const postsWithParams = addParamsToRelatedPost(allSortedResources);

  // featured post
  const getFeaturedPost = filterPostsByCategory(
    [...(data?.posts.nodes || []), ...(data?.learnArticles.nodes || [])],
    'Featured',
  )[0];

  // featured guide
  const getFeaturedGuide = filterPostsByCategory(data.guides.nodes, 'Featured')[0];

  const guideWithPath = getFeaturedGuide
    ? {
        ...getFeaturedGuide,
        path: getFeaturedGuide.guidePostType.guideType
          ? getFeaturedGuide.guidePostType.externalLink.url
          : `${
              filterSinglePostByCategory(getFeaturedGuide, 'Supply')
                ? '/engineers/guides/'
                : '/guides/'
            }${getFeaturedGuide.slug}`,
        target: getFeaturedGuide.guidePostType.guideType ? '_blank' : '_self',
      }
    : '';
  // upcoming webinar
  const getFeaturedWebinar =
    filterPostsByCategory(data.webinars.nodes, 'Featured')[0] ??
    filterPostsByCategory(data.webinars.nodes, 'Upcoming')[0];

  const posts = postsWithParams;
  const webinars = data.webinars.nodes;
  const featuredPost = getFeaturedPost ? getFeaturedPost : '';
  const featuredGuide = guideWithPath ? guideWithPath : '';
  const featuredWebinar = getFeaturedWebinar ? getFeaturedWebinar : '';

  // type name
  const typeName = (post) => {
    if (post.__typename === 'Post' || post.__typename === 'LearnArticle') {
      return 'Blog Post';
    } else {
      return post.__typename;
    }
  };

  const postImage = (post) => {
    return post.featuredImage ? post.featuredImage.node.sourceUrl : sharecardImage.src;
  };

  const postAltText = (post) => {
    return post.featuredImage ? post.featuredImage.node.altText : '';
  };

  // category filter
  const categoryQuery = router.query.category;

  const matchedCategory = CATEGORIES.filter((cat) => cat.slug === categoryQuery)[0]?.label;

  const matchedCategoryDescription = CATEGORIES.filter((cat) => cat.slug === categoryQuery)[0]
    ?.description;

  // if category matches
  if (
    categoryQuery &&
    CATEGORIES.map((cat) => cat.slug).includes(categoryQuery) &&
    categoryQuery !== 'latest'
  ) {
    renderPosts = filterPostsByCategory(posts, matchedCategory);
  } else {
    // filter out featured post, guide, webinar
    renderPosts = posts.filter((post) => {
      return (
        post.id !== featuredPost.id &&
        post.id !== featuredGuide.id &&
        post.id !== featuredWebinar.id
      );
    });
  }

  return (
    <>
      <NextSeo
        title="Resource Center for Remote Leaders - Terminal.io"
        description="Collection of blogs, guide, videos, webinars, and more to help you become a better remote leader."
        canonical={sitePath(router.asPath)}
        {...determineMetaRobots({ translated: false, locale: router.locale })}
        openGraph={{
          type: 'website',
          url: sitePath(router.asPath),
          images: [
            {
              url: sharecardImage.src,
              width: sharecardImage.width,
              height: sharecardImage.height,
              alt: 'Terminal Resource Center',
            },
          ],
        }}
      />
      <SidebarLayout type="businesses">
        {(categoryQuery === 'latest' || !categoryQuery) && (
          <Grid
            templateColumns={{ base: '1fr', md: '1fr 1fr' }}
            gap={{ base: '1.5rem', xl: '2.5rem' }}
          >
            {featuredPost && (
              <GridItem>
                <Link
                  href={`${
                    filterSinglePostByCategory(featuredPost, 'Supply')
                      ? '/engineers/blog/'
                      : '/blog/'
                  }${featuredPost.slug}`}
                  display="block"
                  maxW={{ base: '18.4375rem', sm: '26.875rem' }}
                  ml="auto"
                  mr="auto"
                  role="group"
                  textDecoration="none"
                  _hover={{ textDecoration: 'none' }}
                  aria-label={`Read ${featuredPost.title}`}
                >
                  <Card
                    categoryTitle={`${primaryCategory(featuredPost)} | ${typeName(featuredPost)}`}
                    imageUrl={postImage(featuredPost)}
                    altText={postAltText(featuredPost)}
                    cardTitle={featuredPost.title}
                    size="large"
                    imageIsHighPriority
                  />
                </Link>
              </GridItem>
            )}
            {featuredGuide && (
              <GridItem>
                <Link
                  href={featuredGuide.path}
                  display="block"
                  maxW={{ base: '18.4375rem', sm: '26.875rem' }}
                  ml="auto"
                  mr="auto"
                  role="group"
                  textDecoration="none"
                  _hover={{ textDecoration: 'none' }}
                  target={featuredGuide.target}
                  aria-label={`Read ${featuredGuide.title}`}
                >
                  <Card
                    categoryTitle={`${primaryCategory(featuredGuide)} | ${typeName(featuredGuide)}`}
                    imageUrl={postImage(featuredGuide)}
                    altText={postAltText(featuredGuide)}
                    cardTitle={featuredGuide.title}
                    size="large"
                    imageIsHighPriority
                  />
                </Link>
              </GridItem>
            )}
          </Grid>
        )}

        {categoryQuery && categoryQuery !== 'latest' && (
          <Box>
            <Heading
              as="h1"
              fontSize={{
                base: '1.3rem',
                md: '1.625rem',
              }}
              fontWeight="600"
              lineHeight={{
                base: '1.5rem',
                md: '1.875rem',
              }}
              mt={{ lg: '0.8125rem' }}
              color="#2D445D"
            >
              {matchedCategory}
            </Heading>
            <Text
              mt="0.5rem"
              fontSize="0.875rem"
              lineHeight="1.25rem"
              fontStyle="italic"
              color="#6C6C6C"
            >
              {matchedCategoryDescription}
            </Text>
          </Box>
        )}

        <Box
          w="100%"
          height={{ base: '0.0625rem' }}
          m={{ base: '1.5rem 0', xl: '2.5rem 0' }}
          bgColor="#D8D8D8"
        />

        <Grid
          templateColumns={{
            base: '1fr',
            md: '1fr 1fr',
            xl: '1fr 1fr 1fr',
          }}
          gap={{ base: '2.5rem', lg: '2.5rem 1.865rem' }}
          mb="2.5rem"
        >
          {renderPosts.slice(0, 3).map((post) => {
            return (
              <GridItem key={post.id}>
                <Link
                  href={post.path}
                  display="block"
                  maxW={{ base: '18.4375rem', sm: '26.875rem' }}
                  ml="auto"
                  mr="auto"
                  role="group"
                  textDecoration="none"
                  _hover={{ textDecoration: 'none' }}
                  target={post.target}
                  position="relative"
                  aria-label={`Read ${post.title}`}
                >
                  {filterSinglePostByCategory(post, 'Upcoming') && (
                    <Box
                      position="absolute"
                      top="1.3125rem"
                      right="0"
                      p="0.375rem 0.625rem"
                      fontSize="0.5625rem"
                      lineHeight="0.625rem"
                      letterSpacing="0.0625rem"
                      bgColor="#7AA6B9"
                      color="#fff"
                      textTransform="uppercase"
                      fontWeight="700"
                      zIndex="1"
                    >
                      Register Now
                    </Box>
                  )}
                  <Card
                    categoryTitle={`${primaryCategory(post)} | ${typeName(post)}`}
                    imageUrl={postImage(post)}
                    altText={postAltText(post)}
                    cardTitle={post.title}
                    imageIsHighPriority
                  />
                </Link>
              </GridItem>
            );
          })}
        </Grid>

        {webinars.length && (categoryQuery === 'latest' || !categoryQuery) && featuredWebinar && (
          <UpcomingWebinar
            title={featuredWebinar.title}
            acf={featuredWebinar.webinarsACF}
            href={`/webinars/${featuredWebinar.slug}`}
          />
        )}

        <Grid
          templateColumns={{
            base: '1fr',
            md: '1fr 1fr',
            xl: '1fr 1fr 1fr',
          }}
          gap={{ base: '2.5rem', lg: '2.5rem 1.865rem' }}
        >
          {renderPosts.slice(3, itemsToShow).map((post) => {
            return (
              <GridItem key={post.id}>
                <Link
                  href={post.path}
                  display="block"
                  maxW={{ base: '18.4375rem', sm: '26.875rem' }}
                  ml="auto"
                  mr="auto"
                  role="group"
                  textDecoration="none"
                  _hover={{ textDecoration: 'none' }}
                  target={post.target}
                  position="relative"
                  aria-label={`Read ${post.title}`}
                >
                  {filterSinglePostByCategory(post, 'Upcoming') && (
                    <Box
                      position="absolute"
                      top="1.3125rem"
                      right="0"
                      p="0.375rem 0.625rem"
                      fontSize="0.5625rem"
                      lineHeight="0.625rem"
                      letterSpacing="0.0625rem"
                      bgColor="#7AA6B9"
                      color="#fff"
                      textTransform="uppercase"
                      fontWeight="700"
                      zIndex="1"
                    >
                      Register Now
                    </Box>
                  )}
                  <Card
                    categoryTitle={`${primaryCategory(post)} | ${typeName(post)}`}
                    imageUrl={postImage(post)}
                    altText={postAltText(post)}
                    cardTitle={post.title}
                  />
                </Link>
              </GridItem>
            );
          })}
        </Grid>
        <Box ref={itemsEnd} h="4rem" />

        {/* <ModalBottom title="Get new articles in your inbox every week:" /> */}
      </SidebarLayout>
    </>
  );
}
